import { useAppSelector } from 'flow/hooks/useRedux';

import { TypedMap } from 'flow/types/TypedMap';
import { MobileSubscription, Simcard } from 'flow/types/Extension';
import {
    getRoamingCurrentUsageInCurrencyString,
    getRoamingCutOffPriceString,
} from 'flow/utils/mobile-data-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';

interface Hook {
    simcard: TypedMap<Simcard> | null;
    limit: number | null;
    usage: number | null;
    topUps: {
        total: number;
        used: number;
        available: number;
    };
    roaming: {
        usage: string | null;
        cutOff: string | null;
    };
    isUnlimitedSurf: boolean;
    mobileSubscription: TypedMap<MobileSubscription>;
}

// TODO: #vr #typefix - WEB-14418 - When implementing the other packages for Settings > Mobile, see if this is needed, or can be removed.
const EMPTY: Hook = {
    simcard: null,
    limit: 0,
    usage: 0,
    topUps: {
        total: 0,
        used: 0,
        available: 0,
    },
    roaming: {
        usage: null,
        cutOff: null,
    },
    isUnlimitedSurf: false,
    mobileSubscription: null,
};

const getLimit = (simcard: TypedMap<Simcard>) => {
    const limit = simcard.getIn(['usage', 'surfPackageMaxThisMonth']);

    if (!limit || isNaN(limit)) {
        return 0;
    }

    return limit;
};

const getUsage = (simcard: TypedMap<Simcard>) => {
    const total = simcard.getIn(['usage', 'surfUsedThisMonth']);

    return total;
};

const getTopUps = (subscription: TypedMap<MobileSubscription>) => {
    const total = subscription.get('topUpMaxLimit') || 0;
    const used = subscription.get('topUpThisMonth') || 0;

    return {
        total,
        used,
        available: total - used,
    };
};

const getRoaming = (simcard: TypedMap<Simcard>) => {
    return {
        usage: getRoamingCurrentUsageInCurrencyString(simcard),
        cutOff: getRoamingCutOffPriceString(simcard),
    };
};

export const isUnlimitedSurf = (simcard: TypedMap<Simcard>) =>
    simcard?.getIn(['usage', 'treatAsUnlimited']);

const useMobile = (extensionKey: ExtensionKey) => {
    const mobileSubscription = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            extensionKey,
            'mobileSubscription',
        ]),
    );

    if (!mobileSubscription) {
        return EMPTY;
    }

    const mainSimcard = mobileSubscription
        .get('simcards')
        .find((sc) => sc.get('isMainSimcard'));

    if (!mainSimcard) {
        return EMPTY;
    }

    return {
        simcard: mainSimcard,
        limit: getLimit(mainSimcard),
        usage: getUsage(mainSimcard),
        topUps: getTopUps(mobileSubscription),
        roaming: getRoaming(mainSimcard),
        isUnlimitedSurf: isUnlimitedSurf(mainSimcard),
        mobileSubscription: mobileSubscription,
    };
};

export default useMobile;
